import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import {
    PieChart, Pie, Cell, Tooltip as RTooltip,
    AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer
} from 'recharts';
import { useAuth } from '../../contexts/AuthContexte';
import { useLanguage, getLocale } from '../../contexts/LanguageContext';
import api from '../../api';
import PageHeader from '../ui/PageHeader';
import Icon from '../ui/Icon';
import './MemberPage.css';

// Couleurs de la charte : attaque = violet, défense = vert
const ATK = '#9B6BFF';
const DEF = '#7CCB5B';
const GRID = '#262A35';
const AXIS = '#737A8A';

const fmtNum = (n) => (n || 0).toLocaleString(getLocale()).replace(/ /g, ' ');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(getLocale(), { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const ChartTooltip = ({ active, payload, label, title }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="perso-tooltip">
            {title && <div className="perso-tooltip__title">{title(label, payload)}</div>}
            {payload.map(p => (
                <div key={p.name} className="perso-tooltip__row">
                    <span className="perso-dot" style={{ background: p.color || p.payload?.color }} />
                    {p.name} : <strong>{p.value}</strong>
                </div>
            ))}
        </div>
    );
};

/* ── Répartition attaques / défenses ── */
const AtkDefChart = ({ atkTotal, defTotal }) => {
    const { t } = useLanguage();
    const total = atkTotal + defTotal;
    const data = [
        { name: t('memberStats.attacks', 'Attaques'), value: atkTotal, color: ATK },
        { name: t('memberStats.defenses', 'Défenses'), value: defTotal, color: DEF }
    ];
    return (
        <div className="c2-tile perso-chart">
            <div className="perso-chart__head">
                <h3>{t('memberStats.atkDefTitle', 'Attaques / défenses')}</h3>
                <span className="c2-muted">{total > 1
                    ? t('memberStats.fightsAllSeasons', '{n} combats toutes saisons confondues', { n: total })
                    : t('memberStats.fightAllSeasons', '{n} combat toutes saisons confondues', { n: total })}</span>
            </div>
            <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                    <Pie data={data} cx="50%" cy="50%" innerRadius={60} outerRadius={96} paddingAngle={3} dataKey="value" stroke="none">
                        {data.map(d => <Cell key={d.name} fill={d.color} />)}
                    </Pie>
                    <RTooltip content={<ChartTooltip />} />
                </PieChart>
            </ResponsiveContainer>
            <div className="perso-legend">
                {data.map(d => (
                    <span key={d.name}>
                        <span className="perso-dot" style={{ background: d.color }} />
                        {d.name} <strong>{total ? Math.round((d.value / total) * 100) : 0}%</strong> <span className="c2-muted">({d.value})</span>
                    </span>
                ))}
            </div>
        </div>
    );
};

/* ── Activité par heure ── */
const RANGES = [
    { key: '24h', labelKey: 'memberStats.range24h', label: '24 h', ms: 24 * 3600000 },
    { key: '7d', labelKey: 'memberStats.range7d', label: '7 jours', ms: 7 * 86400000 },
    { key: '30d', labelKey: 'memberStats.range30d', label: '30 jours', ms: 30 * 86400000 }
];
const fmtHour = (h) => `${String(h).padStart(2, '0')}h`;

const HourChart = ({ activityLog }) => {
    const { t } = useLanguage();
    const [range, setRange] = useState('30d');
    const data = useMemo(() => {
        const since = Date.now() - RANGES.find(r => r.key === range).ms;
        const hours = Array.from({ length: 24 }, (_, hour) => ({ hour, atk: 0, def: 0 }));
        const hourOf = new Intl.DateTimeFormat('fr-FR', { hour: 'numeric', hour12: false, timeZone: 'Europe/Paris' });
        activityLog.forEach(e => {
            const d = new Date(e.t);
            if (d.getTime() < since) return;
            const h = parseInt(hourOf.formatToParts(d).find(p => p.type === 'hour')?.value ?? '0', 10) % 24;
            hours[h][e.k === 'a' ? 'atk' : 'def']++;
        });
        return hours;
    }, [activityLog, range]);
    const hasData = data.some(h => h.atk || h.def);

    return (
        <div className="c2-tile perso-chart perso-chart--wide">
            <div className="perso-chart__head">
                <div>
                    <h3>{t('memberStats.activityTitle', 'Horaires d\'activité')}</h3>
                    <span className="c2-muted">{t('memberStats.activitySubtitle', 'Combats par heure · heure de Paris')}</span>
                </div>
                <div className="perso-switch" role="tablist" aria-label={t('memberStats.period', 'Période')}>
                    {RANGES.map(r => (
                        <button key={r.key} type="button" role="tab" aria-selected={range === r.key} className={range === r.key ? 'is-active' : undefined} onClick={() => setRange(r.key)}>
                            {t(r.labelKey, r.label)}
                        </button>
                    ))}
                </div>
            </div>
            {!hasData ? (
                <p className="c2-muted perso-chart__empty">{t('memberStats.noActivity', 'Aucune activité sur cette période')}</p>
            ) : (
                <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={data} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
                        <defs>
                            <linearGradient id="persoAtk" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={ATK} stopOpacity={0.35} />
                                <stop offset="95%" stopColor={ATK} stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="persoDef" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={DEF} stopOpacity={0.35} />
                                <stop offset="95%" stopColor={DEF} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                        <XAxis dataKey="hour" tickFormatter={fmtHour} tick={{ fill: AXIS, fontSize: 11 }} axisLine={false} tickLine={false} interval={2} />
                        <YAxis allowDecimals={false} tick={{ fill: AXIS, fontSize: 11 }} axisLine={false} tickLine={false} />
                        <RTooltip content={<ChartTooltip title={(label) => fmtHour(label)} />} />
                        <Area type="monotone" dataKey="atk" name={t('memberStats.attacks', 'Attaques')} stroke={ATK} strokeWidth={2} fill="url(#persoAtk)" dot={false} />
                        <Area type="monotone" dataKey="def" name={t('memberStats.defenses', 'Défenses')} stroke={DEF} strokeWidth={2} fill="url(#persoDef)" dot={false} />
                    </AreaChart>
                </ResponsiveContainer>
            )}
            <div className="perso-legend">
                <span><span className="perso-dot" style={{ background: ATK }} />{t('memberStats.attacks', 'Attaques')}</span>
                <span><span className="perso-dot" style={{ background: DEF }} />{t('memberStats.defenses', 'Défenses')}</span>
            </div>
        </div>
    );
};

/* ── Détail par saison ── */
const FORMATS = ['1v1', '2v2', '3v3', '4v4', '5v5', '4v5'];

const SeasonTable = ({ seasons }) => {
    const { t } = useLanguage();
    const [mode, setMode] = useState('atk');
    const color = mode === 'atk' ? ATK : DEF;
    const count = (s, f) => s[`${mode}${f}`] || 0;

    return (
        <>
            <div className="perso-switch perso-switch--table" role="tablist" aria-label={t('memberStats.fightType', 'Type de combat')}>
                <button type="button" role="tab" aria-selected={mode === 'atk'} className={mode === 'atk' ? 'is-active' : undefined} onClick={() => setMode('atk')}>{t('memberStats.attacks', 'Attaques')}</button>
                <button type="button" role="tab" aria-selected={mode === 'def'} className={mode === 'def' ? 'is-active' : undefined} onClick={() => setMode('def')}>{t('memberStats.defenses', 'Défenses')}</button>
            </div>
            <div className="c2-table-wrap">
                <table className="c2-table perso-table">
                    <thead>
                        <tr>
                            <th>{t('memberStats.season', 'Saison')}</th>
                            {FORMATS.map(f => <th key={f} className="is-center">{f}</th>)}
                            <th className="is-num">{mode === 'atk' ? t('memberStats.atkPts', 'Pts att.') : t('memberStats.defPts', 'Pts déf.')}</th>
                            <th className="is-center">{t('memberStats.rank', 'Rang')}</th>
                            <th className="is-num">{t('memberStats.total', 'Total')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {seasons.map(s => (
                            <tr key={s._id} className={s.participated ? undefined : 'is-dimmed'}>
                                <td>
                                    <strong>{s.title}</strong>
                                    <span className="perso-table__dates">{fmtDate(s.startDate)} → {fmtDate(s.endDate)}</span>
                                </td>
                                {FORMATS.map(f => (
                                    <td key={f} className="is-center">
                                        {s.participated && count(s, f) > 0
                                            ? <strong style={{ color }}>{count(s, f)}</strong>
                                            : <span className="c2-muted">—</span>}
                                    </td>
                                ))}
                                <td className="is-num">
                                    {s.participated ? <strong style={{ color }}>{fmtNum(mode === 'atk' ? s.atkPoints : s.defPoints)}</strong> : <span className="c2-muted">—</span>}
                                </td>
                                <td className="is-center">
                                    {s.myRank ? <span className={`perso-rank${s.myRank <= 3 ? ` perso-rank--${s.myRank}` : ''}`}>#{s.myRank}</span> : <span className="c2-muted">—</span>}
                                </td>
                                <td className="is-num"><strong>{s.myPoints != null ? fmtNum(s.myPoints) : '—'}</strong></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};

const MemberPage = () => {
    const { serverId } = useParams();
    const { user } = useAuth();
    const { t } = useLanguage();
    const { server } = useOutletContext() || {};
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        setLoading(true);
        api.get(`/stats/${serverId}/me`)
            .then(res => setStats(res.data))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [serverId]);

    const c = stats?.combats || {};
    const seasons = stats?.seasonHistory || [];
    const participated = seasons.filter(s => s.participated);
    const bestRank = participated.reduce((best, s) => (s.myRank && (best === null || s.myRank < best) ? s.myRank : best), null);
    const avgPts = participated.length ? Math.round(participated.reduce((sum, s) => sum + (s.myPoints || 0), 0) / participated.length) : 0;
    const bestPts = participated.reduce((best, s) => Math.max(best, s.myPoints || 0), 0);

    const kpis = [
        { icon: 'swords', label: t('memberStats.attacks5v5', 'Attaques 5v5'), value: fmtNum(c.atk5v5), sub: t('memberStats.in4v5', '{n} en 4v5', { n: c.atk4v5 || 0 }), tone: 'purple' },
        { icon: 'shield', label: t('memberStats.defenses5v5', 'Défenses 5v5'), value: fmtNum(c.def5v5), sub: t('memberStats.in4v5', '{n} en 4v5', { n: c.def4v5 || 0 }), tone: 'green' },
        { icon: 'star', label: t('memberStats.totalPoints', 'Total points'), value: fmtNum(c.totalPts), sub: t('memberStats.allSeasons', 'toutes saisons') },
        { icon: 'trophy', label: t('memberStats.bestRank', 'Meilleur rang'), value: bestRank ? `#${bestRank}` : '—', sub: t('memberStats.allSeasons', 'toutes saisons') },
        {
            icon: 'chart',
            label: t('memberStats.avgPerSeason', 'Moyenne / saison'),
            value: fmtNum(avgPts),
            sub: participated.length > 1
                ? t('memberStats.seasonsPlayed', '{n} saisons jouées', { n: participated.length })
                : t('memberStats.seasonPlayed', '{n} saison jouée', { n: participated.length })
        },
        { icon: 'arrowRight', label: t('memberStats.bestScore', 'Meilleur score'), value: fmtNum(bestPts), sub: t('memberStats.inOneSeason', 'en une saison') }
    ];

    const avatar = user?.avatar ? `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png?size=96` : null;

    return (
        <div className="c2-scope c2-admin-page">
            <PageHeader icon="user" title={t('member.nav.stats', 'Stats perso')} subtitle={`${user?.username || ''}${server?.name ? ` · ${server.name}` : ''}`} />


            {loading ? (
                <div className="c2-loading">
                    <div className="c2-spinner" />
                    <p>{t('memberStats.loading', 'Chargement des statistiques...')}</p>
                </div>
            ) : error ? (
                <p className="perso-error" role="alert">{t('memberStats.loadError', 'Impossible de charger tes statistiques.')}</p>
            ) : participated.length === 0 ? (
                <div className="c2-card c2-empty perso-empty">
                    {avatar ? <img className="perso-avatar" src={avatar} alt="" /> : <Icon name="user" size={36} />}
                    <p>{t('memberStats.emptyHint', 'Tes statistiques apparaîtront à la fin de ta première saison.')}</p>
                </div>
            ) : (
                <>
                    <div className="perso-kpis">
                        {kpis.map(k => (
                            <div key={k.label} className={`c2-tile perso-kpi${k.tone ? ` perso-kpi--${k.tone}` : ''}`}>
                                <span className="perso-kpi__label"><Icon name={k.icon} size={16} />{k.label}</span>
                                <span className="perso-kpi__value">{k.value}</span>
                                <span className="perso-kpi__sub">{k.sub}</span>
                            </div>
                        ))}
                    </div>

                    {(c.atkTotal > 0 || c.defTotal > 0) && (
                        <section className="c2-card c2-section">
                            <h2 className="c2-section__title"><Icon name="chart" size={20} />{t('memberStats.splitActivity', 'Répartition et activité')}</h2>
                            <div className="perso-charts">
                                <AtkDefChart atkTotal={c.atkTotal || 0} defTotal={c.defTotal || 0} />
                                <HourChart activityLog={stats.activityLog || []} />
                            </div>
                        </section>
                    )}

                    <section className="c2-card c2-section">
                        <h2 className="c2-section__title"><Icon name="trophy" size={20} />{t('memberStats.perSeason', 'Détail par saison')}</h2>
                        <SeasonTable seasons={seasons} />
                    </section>
                </>
            )}
        </div>
    );
};

export default MemberPage;
